import { Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import Home from "../pages/Home";
import About from "../pages/About";
import Contact from "../pages/Contact";
import Cart from "../pages/Cart";
import Login from "../pages/Login";
import Questionnaire from "pages/Questionnaire";
import HerbalPlan from "pages/HerbalPlan";
import Consent from "pages/Consent";
import ConsultationPayment from "pages/ConsultationPayment";
import ValidatePayment from "pages/ValidatePayment";
import ScheduleMeeting from "pages/ScheduleMeeting";
import AcuityScheduling from "pages/AcuityScheduling";
import TermsAndConditions from "pages/TermsAndConditions";
import PrivacyPolicy from "pages/PrivacyPolicy";
import SubscriptionPage from "pages/Subscription";
import PrescriptionPage from "pages/Prescription";
// [Feature 1] — New pages for consultation pathway
import ConsultationOptions from "pages/ConsultationOptions";
import PaymentAuthorization from "pages/PaymentAuthorization";
import SubscriptionCheckIn from "pages/SubscriptionCheckIn";
import { useQuery } from "@tanstack/react-query";
import { getUserProfileApi } from "services/authService";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <div>Loading...</div>;
  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Subscription Check Component: Checks subscription eligibility before rendering
const SubscriptionCheck = ({ children }) => {
  const { isAuthenticated, user } = useAuth();

  const { data: profileData } = useQuery({
    queryKey: ["userProfile"],
    queryFn: getUserProfileApi,
    enabled: Boolean(isAuthenticated),
    staleTime: 1000 * 60 * 2,
  });

  const isEligible = Boolean(
    profileData?.payload?.isEligibleForSubscription ??
    (user?.isEligibleForSubscription === true ||
      user?.isEligibleForSubscription === "true")
  );

  if (!isEligible) {
    return <Navigate to="/" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/login" element={<Login />} />
      <Route path="/about" element={<About />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
      <Route path="/cart" element={<Cart />} />
      <Route
        path="/questionnaire"
        element={
          <ProtectedRoute>
            <Questionnaire />
          </ProtectedRoute>
        }
      />
      <Route
        path="/herbal-plan"
        element={
          <ProtectedRoute>
            <HerbalPlan />
          </ProtectedRoute>
        }
      />
      <Route
        path="/consent/:submissionId"
        element={
          <ProtectedRoute>
            <Consent />
          </ProtectedRoute>
        }
      />
      <Route
        path="/consultation-fee/:submissionId"
        element={
          <ProtectedRoute>
            <ConsultationPayment />
          </ProtectedRoute>
        }
      />
      <Route
        path="/:submissionId/validate-payment"
        element={
          <ProtectedRoute>
            <ValidatePayment />
          </ProtectedRoute>
        }
      />
      <Route
        path="/:submissionId/schedule-meeting"
        element={
          <ProtectedRoute>
            <ScheduleMeeting />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
      <Route
        path="/schedule"
        element={
          <ProtectedRoute>
            <AcuityScheduling />
          </ProtectedRoute>
        }
      />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route
        path="/subscription"
        element={
          <ProtectedRoute>
            <SubscriptionCheck>
              <SubscriptionPage />
            </SubscriptionCheck>
          </ProtectedRoute>
        }
      />
      <Route
        path="/hua-prescription"
        element={
          <ProtectedRoute>
            <PrescriptionPage />
          </ProtectedRoute>
        }
      />
      {/* [Feature 1] — Consultation pathway new routes */}
      <Route
        path="/consultation-options"
        element={
          <ProtectedRoute>
            <ConsultationOptions />
          </ProtectedRoute>
        }
      />
      <Route
        path="/payment-authorization"
        element={
          <ProtectedRoute>
            <PaymentAuthorization />
          </ProtectedRoute>
        }
      />
      {/* Monthly Subscription Check-in Form (Public) */}
      <Route
        path="/subscription/check-in"
        element={<SubscriptionCheckIn />}
      />
    </Routes>
  );
}

export default AppRoutes;
